import { AccessCase, AccessCaseStatus, AccessSource } from '@prisma/client';
import { Badge, Button, Spin, Tooltip, Typography } from 'antd';

import { formatIsoDate } from '@app/utils-agnostic';
import { Theme } from '@app/web-components';

import { useAccessCaseFind } from '../data/access-case-data';

export type CaseRendererProps =
  | {
      userId: number;
      accessId: number;
      access?: never;
      onOpenCaseView: (caseId: number) => void;
    }
  | {
      userId: number;
      accessId?: never;
      access: { externalId: string; accessSource: AccessSource };
      onOpenCaseView: (caseId: number) => void;
    };

export const CaseRenderer = ({ userId, accessId, access, onOpenCaseView }: CaseRendererProps) => {
  const { token } = Theme.useToken();
  const { data, error, isPending, isFetching } = useAccessCaseFind<AccessCase & { _count: { notices: number } }>({
    where: accessId
      ? {
          accessId,
          status: AccessCaseStatus.OPEN,
        }
      : {
          access: { externalId: access?.externalId, accessSource: access?.accessSource },
          status: AccessCaseStatus.OPEN,
        },
    include: {
      _count: {
        select: { notices: true },
      },
    },
  });

  if (isPending || isFetching) {
    return <Spin size="small" />;
  }

  if (error) {
    return <div>Error...</div>;
  }

  const userCases = data?.filter((x) => x.userId === userId);

  if (!userCases || !userCases.length) {
    return '';
  }

  const openedDate = formatIsoDate(userCases[0].createdOn.toString(), false);

  let toolTip = 'Notified 0 times.';
  if (userCases[0]._count.notices === 1) {
    toolTip = 'Notified 1 time.';
  } else if (userCases[0]._count.notices > 1) {
    toolTip = `Notified ${userCases[0]._count.notices} times.`;
  }

  return (
    <Tooltip title={toolTip}>
      <Button size="small" type="default" onClick={() => onOpenCaseView(userCases[0].id)}>
        <Badge count={userCases[0]._count.notices} offset={[12, 4]} color={token['bright-error']}>
          <Typography.Text>Opened: {openedDate}</Typography.Text>
        </Badge>
      </Button>
    </Tooltip>
  );
};
